import { useMemo } from "react";
import { useWatch } from "react-hook-form";

import { useCurrentComposition, useCompositionForm } from "@design-lab/entities/composition";
import { useIsSample, useDesignLabConfig } from "@design-lab/shared/lib/config";
import type { Color } from "@design-lab/shared/types/base";

export function useProductColors({ maxColors }: { maxColors: number } = { maxColors: 30 }) {
  const { currentProduct } = useCurrentComposition();
  const { form } = useCompositionForm();
  const isSample = useIsSample();
  const config = useDesignLabConfig();
  const madeSamplesV1 = config.flip?.madeSamplesV1;

  const currentCompositionId = useWatch({
    name: "currentCompositionId",
    control: form.control
  });

  const addedCompositions = form.getValues("addedCompositions");
  const addedCompositionIds = useMemo(
    () => new Set((addedCompositions || []).map(comp => comp.id)),
    [addedCompositions]
  );

  const colors: Color[] = useMemo(() => {
    const { compositions } = currentProduct || {};
    if (!compositions) return [];

    const filteredCompositions = compositions.filter(comp => {
      if (comp.outOfStock) return false;

      if (isSample && !madeSamplesV1) {
        return comp.availableForSample === true;
      }

      return true;
    });

    return filteredCompositions.map(comp => ({
      id: comp.id,
      name: comp.colorName,
      hex: comp.colorHex || "#000000",
      swatchUrl: comp.swatchUrl,
      materials: comp.materials,
      selected: false,
      isJiffy1st: comp.jiffy1st ?? false
    }));
  }, [currentProduct, isSample, madeSamplesV1]);

  const allColors = colors.map(color => ({
    ...color,
    selected: color.id === currentCompositionId
  }));

  const recommendedColors: Color[] = [];
  const otherColors: Color[] = [];

  allColors.forEach(color => {
    if (color.isJiffy1st) {
      recommendedColors.push(color);
    } else {
      otherColors.push(color);
    }
  });

  const selectedColor = allColors.find(c => c.id === currentCompositionId);

  return {
    colors: otherColors,
    recommendedColors,
    selectedColor,
    addedCompositionIds,
    currentProduct,
    maxVisibleColors: maxColors,
    isShowAllAvailable: allColors.length > maxColors
  };
}
